import React from 'react';
import * as Sentry from '@sentry/react';
import i18n from '../../i18n';
import { captureException, isSentryEnabled } from '../../utils/sentry';
import { recordAuthEvent } from '../../utils/authJournal';

const _CHUNK_ERROR_RE = /Failed to fetch dynamically imported module|Importing a module script failed|dynamically imported module/i;
const _CHUNK_RELOAD_KEY = 'grid_chunk_reload_at';
const _CHUNK_RELOAD_THROTTLE_MS = 10_000;

function _isChunkError(error) {
  return _CHUNK_ERROR_RE.test(error?.message || '');
}

function _tryAutoReload() {
  const last = sessionStorage.getItem(_CHUNK_RELOAD_KEY);
  if (last && Date.now() - parseInt(last, 10) < _CHUNK_RELOAD_THROTTLE_MS) {
    return false;
  }
  sessionStorage.setItem(_CHUNK_RELOAD_KEY, String(Date.now()));
  recordAuthEvent('reload', { reason: 'error-boundary-chunk', withStack: false });
  window.location.reload();
  return true;
}

/**
 * Error Boundary component that catches React errors and reports them to Sentry
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      eventId: null
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  async componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    if (_isChunkError(error)) {
      _tryAutoReload();
      return;
    }

    this.setState({
      error,
      errorInfo
    });

    // Check if Sentry is enabled before capturing
    const sentryEnabled = await isSentryEnabled();
    
    if (sentryEnabled) {
      // Capture the error with Sentry
      Sentry.withScope((scope) => {
        scope.setTag('errorBoundary', this.props.name || 'unknown');
        scope.setLevel('error');
        scope.setContext('errorInfo', errorInfo);
        scope.setContext('props', this.props);
        
        const eventId = Sentry.captureException(error);
        this.setState({ eventId });
      });
    } else {
      // Fallback: just log to console if Sentry is disabled
      console.error('Error captured by ErrorBoundary (Sentry disabled):', {
        error: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        boundary: this.props.name || 'unknown'
      });
    }
  }

  handleRetry = () => {
    if (_isChunkError(this.state.error)) {
      window.location.reload();
      return;
    }

    // Reset error state first so the component tree can re-render
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      eventId: null
    });

    // Force the router to re-mount the current route via SPA navigation,
    // avoiding a full page reload. This breaks the FM-003 cycle where
    // re-rendering the same broken tree re-triggers auth/network-request-failed.
    try {
      window.history.pushState({}, '', window.location.pathname + window.location.search);
      window.dispatchEvent(new PopStateEvent('popstate', { state: {} }));
    } catch {
      // Fallback: if history manipulation fails, do nothing — setState above
      // already cleared the error boundary, which is the minimum viable retry.
    }

    // Fire optional parent-supplied callback (e.g. to trigger a re-fetch or re-auth)
    if (typeof this.props.onRetry === 'function') {
      this.props.onRetry();
    }
  };

  handleReportFeedback = () => {
    if (this.state.eventId) {
      Sentry.showReportDialog({ eventId: this.state.eventId });
    }
  };

  render() {
    if (this.state.hasError) {
      // Custom error UI
      if (this.props.fallback) {
        return this.props.fallback(
          this.state.error,
          this.state.errorInfo,
          this.handleRetry
        );
      }

      // Default error UI
      return (
        <div className="min-h-screen flex items-center justify-center bg-surface-2">
          <div className="max-w-md w-full bg-surface shadow-lg rounded-lg p-6">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <svg
                  className="h-8 w-8 text-red-400 dark:text-red-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-ink">
                  {i18n.t('error_boundary_title')}
                </h3>
              </div>
            </div>

            <div className="mb-4">
              <p className="text-sm text-ink-muted">
                {i18n.t('error_boundary_message')}
              </p>
            </div>

            {import.meta.env.MODE === 'development' && this.state.error && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded">
                <h4 className="text-sm font-medium text-red-800 dark:text-red-200 mb-2">
                  {i18n.t('error_boundary_details')}
                </h4>
                <p className="text-xs text-red-700 dark:text-red-300 font-mono break-all">
                  {this.state.error.message}
                </p>
                {this.state.error.stack && (
                  <details className="mt-2">
                    <summary className="text-xs text-red-600 dark:text-red-300 cursor-pointer">
                      {i18n.t('error_boundary_stack')}
                    </summary>
                    <pre className="text-xs text-red-700 dark:text-red-300 mt-1 whitespace-pre-wrap">
                      {this.state.error.stack}
                    </pre>
                  </details>
                )}
              </div>
            )}

            <div className="flex space-x-3">
              <button
                onClick={this.handleRetry}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {i18n.t('error_boundary_try_again')}
              </button>

              {this.state.eventId && (
                <button
                  onClick={this.handleReportFeedback}
                  className="flex-1 bg-gray-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  {i18n.t('error_boundary_report')}
                </button>
              )}
            </div>

            {this.props.name && (
              <p className="mt-3 text-xs text-ink-subtle text-center">
                {i18n.t('error_boundary_in')} {this.props.name}
              </p>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Higher-order component to wrap components with error boundary
 * @param {React.Component} Component - Component to wrap
 * @param {string} name - Name for the error boundary
 * @returns {React.Component} Wrapped component
 */
export const withErrorBoundary = (Component, name = Component.name || 'Unknown') => {
  const WrappedComponent = (props) => (
    <ErrorBoundary name={name}>
      <Component {...props} />
    </ErrorBoundary>
  );
  
  WrappedComponent.displayName = `withErrorBoundary(${name})`;
  return WrappedComponent;
};

/**
 * Sentry's built-in error boundary (alternative option)
 */
export const SentryErrorBoundary = Sentry.ErrorBoundary;

export default ErrorBoundary; 